import { useEffect, useRef, useState } from 'react'
import { type Collection } from '../api/client'
import { exportJson, exportPdf, exportText } from '../utils/export'

export function ExportMenu({
  handle,
  collection,
  count,
}: {
  handle: string
  collection: Collection
  count: number
}) {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  const disabled = count === 0 || !!busy

  async function run(fmt: 'text' | 'json' | 'pdf') {
    setOpen(false)
    setBusy(fmt)
    setProgress(null)
    try {
      if (fmt === 'json') await exportJson(handle, collection)
      else if (fmt === 'text') await exportText(handle, collection)
      else
        await exportPdf(handle, collection, (done, total) => {
          setProgress({ done, total })
        })
    } catch (err) {
      console.error('export failed', err)
    } finally {
      setBusy(null)
      setProgress(null)
    }
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        className="glass-button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        title={count === 0 ? 'Nothing cached yet' : 'Export cached records'}
      >
        {busy
          ? progress
            ? `${busy.toUpperCase()} ${progress.done}/${progress.total}`
            : `Generating ${busy}…`
          : 'Export ▾'}
      </button>
      {open ? (
        <div className="glass-strong absolute right-0 z-20 mt-2 w-48 overflow-hidden p-1 text-sm">
          {(['text', 'json', 'pdf'] as const).map((fmt) => (
            <button
              key={fmt}
              type="button"
              onClick={() => run(fmt)}
              className="block w-full rounded-lg px-3 py-2 text-left hover:bg-white/10"
            >
              {fmt === 'pdf' ? 'PDF (text + images)' : fmt.toUpperCase()}
            </button>
          ))}
          <p className="px-3 py-2 text-[10px] text-ink-300">
            All export happens locally in your browser. Videos are skipped from PDF.
          </p>
        </div>
      ) : null}
    </div>
  )
}
